import { createDictation, dictationSupported, type Dictation } from "./speech";

/**
 * Compozitorul - bucata de chat pe care o folosesc AMBELE interfețe: widgetul
 * vizitatorului (`widget.ts`) și panoul de configurare (`builder/index.ts`).
 * Există ca să se comporte identic în cele două locuri, nu ca să economisească
 * linii: câmpul care crește, Enter/Shift+Enter, butonul tăcut care se colorează
 * doar când are ce trimite, microfonul care alunecă sub text - toate erau
 * reimplementate de două ori și divergeau la fiecare atingere.
 *
 * Ce NU intră aici: mesajele, bula de „gândește", istoricul. Alea sunt legate de
 * transportul fiecărei părți (widgetul are stop + reveal progresiv, panoul are
 * scan + upload), deci rămân la ele.
 *
 * Diferențele care rămân legitime sunt opțiuni, nu ramuri de cod:
 *  - `maxHeightPx` - panoul își plafonează creșterea la jumătate din fereastră (funcție,
 *    recalculată la fiecare tastă), widgetul la un plafon fix, mai mic (108px, apoi scroll);
 *  - `stoppable` - doar widgetul poate opri un răspuns în curs;
 *  - `mic` - deocamdată doar panoul; vezi nota de la ComposerOptions.mic.
 *
 * Tematizarea trece prin `--aigc-*`, setate de fiecare gazdă pe rândul propriu:
 * widgetul le leagă de culoarea clientului, panoul de indigo-ul nostru.
 */

/* CSS-ul e o literală de șablon: comentariile DIN el ajung verbatim în bundle
   (esbuild nu le atinge), deci sunt în engleză - vezi CLAUDE.md. */
export const COMPOSER_CSS = `
/* One bordered field rather than a strip with a divider above it: the border is what
   separates the composer from the conversation, so no extra rule is needed.
   align-items: flex-end keeps the buttons on the last line as the field grows upwards,
   the way every chat people already use behaves.
   margin: 0 is not decoration - the browser's own stylesheet puts 1em under every form,
   which showed up as a dead 13px band underneath. */
.aigc-form {
  display: flex; align-items: flex-end; gap: 6px; margin: 0; padding: 4px 4px 4px 10px;
  border: 1px solid var(--aigc-line, #e2e8f0); border-radius: 12px; background: #fff;
  flex-shrink: 0; transition: border-color .14s;
}
/* Focus, deliberately. The text field gets no ring of its own: a textarea matches
   :focus-visible even on a plain mouse click, so an outline there would box the composer
   on every single click. The border darkens instead - always visible while the field is
   active, never a rectangle in the way.
   Grey, not the brand colour: the border is chrome, and every chat people already use
   greys it. On a customer's own site an accented outline read as a second brand shouting
   next to theirs, and it fought the send arrow, which is the one thing here that has
   earned colour. Darker on focus rather than lighter, so the active field is the one
   that advances. */
.aigc-form:focus-within { border-color: var(--aigc-line-focus, #94a3b8); }
.aigc-input:focus, .aigc-input:focus-visible { outline: none; }
/* The mic and the text share a relative wrapper: the mic is positioned under the field,
   which slides over it (margin-left goes to 0) as soon as the first character appears,
   and retreats when the field is emptied. Without a mic the wrapper is inert. */
.aigc-field { position: relative; flex: 1; min-width: 0; display: flex; }
.aigc-input {
  flex: 1; width: 100%; border: none; padding: 6px 0; font-family: inherit;
  font-size: var(--aigc-font-size, 13px); line-height: 1.45;
  background: transparent; color: #111; resize: none; overflow-y: hidden;
}
.aigc-input::placeholder { color: #94a3b8; opacity: 1; font-style: normal; }
.aigc-input:disabled { color: #94a3b8; }
.aigc-field--mic .aigc-mic { position: absolute; left: 0; bottom: 0; z-index: 1; }
.aigc-field--mic .aigc-input {
  position: relative; z-index: 2; box-sizing: border-box; background: #fff;
  margin-left: 30px; transition: margin-left .22s cubic-bezier(.4,0,.2,1);
}
.aigc-field--mic .aigc-input--full { margin-left: 0; }
/* Both buttons are deliberately quiet: they sit inside the field, and a filled disc there
   competes with the conversation for attention every second it is on screen. The send
   button earns colour only when there is something to send, and a soft tint under the
   pointer. The 3.5px lift is the exact half-difference between the field's one-line height
   and the button's: flex-end pins them to the bottom, which is right once the field has
   grown, but leaves them sitting low against a single line. Measured, not guessed. */
.aigc-mic, .aigc-send {
  flex-shrink: 0; width: 24px; height: 24px; border-radius: 7px; border: none; padding: 0;
  margin-bottom: 3.5px; background: none; color: #94a3b8;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  font-family: inherit; appearance: none; -webkit-appearance: none;
  transition: background .14s, color .14s;
}
.aigc-mic svg, .aigc-send svg { width: 14px; height: 14px; display: block; }
.aigc-mic:hover:not(:disabled), .aigc-send:hover:not(:disabled) { background: #f1f5f9; }
.aigc-send:not(:disabled) { color: var(--aigc-accent, #7a51d8); }
.aigc-send:disabled { color: #cbd5e1; cursor: default; }
.aigc-mic:disabled { color: #cbd5e1; cursor: default; }
/* Recording is the one state loud enough to earn a filled button: it is a privilege the
   visitor granted a moment ago, and it must be obvious that it is still in effect. */
.aigc-mic.aigc-mic--live { background: #dc2626; color: #fff; animation: aigc-pulse 1.2s ease-in-out infinite; }
.aigc-mic.aigc-mic--live:hover:not(:disabled) { background: #b91c1c; }
@keyframes aigc-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(220,38,38,.45); } 50% { box-shadow: 0 0 0 6px rgba(220,38,38,0); } }
/* Buttons do only match :focus-visible on keyboard, so a real ring is right there. Drawn
   inset so it never escapes the field's rounded corners. */
.aigc-mic:focus-visible, .aigc-send:focus-visible { outline: 2px solid var(--aigc-accent, #7a51d8); outline-offset: -2px; }
@media (prefers-reduced-motion: reduce) {
  .aigc-form, .aigc-mic, .aigc-send, .aigc-field--mic .aigc-input { transition: none; }
  .aigc-mic.aigc-mic--live { animation: none; }
}
`;

/** Săgeata de trimitere și pătratul de oprire - currentColor, ca să urmeze cerneala butonului. */
const SEND_ICON =
  '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
  '<path d="M8 13V3M8 3L3.5 7.5M8 3l4.5 4.5" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round"/></svg>';
// Pătratul e mai plin decât săgeata, nu din inconsecvență: oprirea e o acțiune pe care
// o cauți sub presiune, iar la 14px un pătrat de aceeași greutate optică s-ar citi ca un punct.
const STOP_ICON =
  '<svg viewBox="0 0 16 16" aria-hidden="true">' +
  '<rect x="3.5" y="3.5" width="9" height="9" rx="2" fill="currentColor"/></svg>';
// Microfonul standard (contur, stil Lucide) - currentColor preia culoarea butonului,
// inclusiv albul din starea de înregistrare.
const MIC_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>' +
  '<path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>';

/** Codurile de eroare de dictare pe care gazda le traduce în textul ei. */
export type MicErrorKind = "denied" | "no-speech" | "network" | "failed";

/** Cât rămâne microfonul descoperit după ce dictarea s-a oprit - vezi micHeldVisible(). */
const MIC_SETTLE_MS = 500;

export interface ComposerLabels {
  placeholder: string;
  send: string;
  stop?: string;
  mic?: string;
  micListening?: string;
}

export interface ComposerOptions {
  labels: ComposerLabels;
  onSubmit: (text: string) => void;
  /**
   * Cât poate crește câmpul înainte să deruleze intern; `null` = fără plafon.
   * O funcție se recalculează la fiecare `sync()` - plafonul panoului urmărește
   * înălțimea disponibilă (ecranul se poate roti/redimensiona cât panoul e deschis).
   */
  maxHeightPx?: number | null | (() => number);
  maxLength?: number;
  /**
   * Microfon (Web Speech API). Deocamdată pornit DOAR în panoul de configurare.
   * Recunoașterea NU e locală: Chrome trimite audio la Google, Safari la Apple -
   * deci pornirea lui în widgetul vizitatorului adaugă o categorie nouă de date
   * (vocea end-userului) și un destinatar nou, ceea ce cere privacy §5 + DPA §6
   * Annex III și notificarea la 30 de zile. Vezi docs/legal-register.md.
   */
  mic?: boolean;
  /** Butonul devine „stop" cât timp răspunsul e pe drum (widget). */
  stoppable?: boolean;
  /** Butoane proprii ale gazdei, așezate în câmp înaintea textului (agrafa din panou). */
  extras?: HTMLElement[];
  onStop?: () => void;
  onMicError?: (kind: MicErrorKind) => void;
  /** Enter trimite; pe touch NU - acolo Enter e singurul mod de a face un paragraf. */
  submitOnEnter?: boolean | (() => boolean);
}

export class Composer {
  readonly form: HTMLFormElement;
  readonly input: HTMLTextAreaElement;
  private readonly sendBtn: HTMLButtonElement;
  private readonly micBtn: HTMLButtonElement | null = null;
  private dictation: Dictation | null = null;
  private dictationBase = "";
  /** Cât timp rulează, microfonul nu are voie să fie acoperit - vezi micHeldVisible(). */
  private micSettleTimer?: ReturnType<typeof setTimeout>;
  private busy = false;
  private blocked = false;
  private refocusAfterBusy = false;

  constructor(private readonly opts: ComposerOptions) {
    this.form = document.createElement("form");
    this.form.className = "aigc-form";

    this.input = document.createElement("textarea");
    this.input.className = "aigc-input";
    this.input.rows = 1;
    this.input.placeholder = opts.labels.placeholder;
    if (opts.maxLength) this.input.maxLength = opts.maxLength;
    this.input.addEventListener("input", () => this.sync());
    this.input.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" || e.shiftKey || !this.enterSubmits()) return;
      e.preventDefault();
      this.form.requestSubmit();
    });

    const field = document.createElement("div");
    field.className = "aigc-field";
    if (opts.mic && dictationSupported()) {
      field.classList.add("aigc-field--mic");
      this.micBtn = document.createElement("button");
      this.micBtn.className = "aigc-mic";
      this.micBtn.type = "button";
      this.micBtn.innerHTML = MIC_ICON;
      this.micBtn.title = opts.labels.mic ?? "Dictate";
      this.micBtn.setAttribute("aria-label", opts.labels.mic ?? "Dictate");
      this.micBtn.addEventListener("click", () => this.toggleDictation());
      field.appendChild(this.micBtn);
    }
    field.appendChild(this.input);

    this.sendBtn = document.createElement("button");
    this.sendBtn.className = "aigc-send";
    this.sendBtn.type = "submit";

    if (opts.extras?.length) this.form.append(...opts.extras);
    this.form.append(field, this.sendBtn);
    this.sync();

    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      // Cât timp răspunsul e pe drum, același buton oprește în loc să trimită.
      if (this.busy && this.opts.stoppable) {
        this.opts.onStop?.();
        return;
      }
      // Ocupat fără stop (panoul): submit-ul nu retrimite. Butonul e oricum mort,
      // dar formularul se poate depune și altfel, iar un al doilea mesaj plecat în
      // timpul primului l-ar dubla.
      const text = this.input.value.trim();
      if (!text || this.blocked || this.busy) return;
      this.setValue("");
      this.opts.onSubmit(text);
    });
  }

  /** Tastatură soft (telefon/tabletă): acolo Enter face rând nou, nu trimite. */
  private enterSubmits(): boolean {
    const rule = this.opts.submitOnEnter ?? true;
    if (typeof rule === "function") return rule();
    if (!rule) return false;
    return !(typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches);
  }

  /**
   * Orice mutație a lui `value` (tastat, dictat, trimis, restaurat pe eroare) trebuie
   * să treacă pe aici: înălțimea, alunecarea peste microfon și starea butonului se
   * recalculează toate în același loc. Reset la `auto` întâi - altfel câmpul n-ar mai
   * scădea la ștergere.
   */
  private sync(): void {
    const capOpt = this.opts.maxHeightPx === undefined ? 108 : this.opts.maxHeightPx;
    const cap = typeof capOpt === "function" ? capOpt() : capOpt;
    this.input.style.height = "auto";
    const wanted = this.input.scrollHeight;
    // `scrollHeight` e 0 cât timp câmpul nu e încă în pagină (construcție) sau e
    // ascuns (panoul închis). O înălțime calculată atunci ar fi `0px` și ar tăia
    // chiar și placeholderul - la repaus lăsăm CSS-ul și `rows=1` să-l dimensioneze.
    if (wanted > 0) {
      this.input.style.height = `${cap == null ? wanted : Math.min(wanted, cap)}px`;
      this.input.style.overflowY = cap != null && wanted > cap ? "auto" : "hidden";
    }
    this.input.classList.toggle("aigc-input--full", this.input.value.length > 0 && !this.micHeldVisible());

    const stopping = this.busy && !!this.opts.stoppable;
    this.sendBtn.innerHTML = stopping ? STOP_ICON : SEND_ICON;
    const label = stopping ? (this.opts.labels.stop ?? "Stop") : this.opts.labels.send;
    this.sendBtn.setAttribute("aria-label", label);
    // Gol și inactiv: butonul n-are ce trimite. Cât timp e ocupat rămâne activ doar
    // dacă poate opri - altfel e la fel de mort ca restul rândului.
    this.sendBtn.disabled = stopping ? false : this.blocked || this.busy || this.input.value.trim() === "";
  }

  setValue(text: string): void {
    this.input.value = text;
    this.sync();
  }

  get value(): string {
    return this.input.value;
  }

  focus(): void {
    // preventScroll: la focusul de după o navigare, pagina abia încărcată nu
    // are voie să sară din poziția ei - inputul e oricum fix, în overlay.
    this.input.focus({ preventScroll: true });
  }

  /**
   * Răspunsul e pe drum. Dezactivarea câmpului îi ia focusul, așa că îl reținem ca
   * să-l dăm înapoi la final - altfel ar trebui un click în plus înainte de fiecare
   * întrebare următoare.
   */
  setBusy(busy: boolean): void {
    this.busy = busy;
    this.applyEnabled();
  }

  /** Blocaj din altă parte a gazdei (scanarea, în panou): compozitorul tace de tot. */
  setBlocked(blocked: boolean): void {
    this.blocked = blocked;
    this.applyEnabled();
  }

  private applyEnabled(): void {
    const off = this.busy || this.blocked;
    if (off && !this.input.disabled) {
      this.refocusAfterBusy = this.input.getRootNode() instanceof ShadowRoot
        ? (this.input.getRootNode() as ShadowRoot).activeElement === this.input
        : document.activeElement === this.input;
    }
    this.input.disabled = off;
    if (this.micBtn) this.micBtn.disabled = off;
    // Dictarea nu supraviețuiește dezactivării câmpului: ar continua să scrie într-un
    // input mort și microfonul ar rămâne aprins fără nimic care să-l oprească.
    if (off && this.dictation?.listening) this.dictation.stop();
    this.sync();
    if (!off && this.refocusAfterBusy) {
      this.refocusAfterBusy = false;
      this.input.focus();
    }
  }

  // --- dictare (Web Speech API) ---

  /**
   * Microfonul nu are voie să fie acoperit cât dictează - și încă o clipă după.
   *
   * Textul alunecă peste el la primul caracter, iar în dictare caracterele vin CHIAR
   * DE LA EL: se ascundea singur exact când era singurul mod de a-l opri. Nu e doar
   * incomod - `continuous = true`, deci recunoașterea nu se închide la tăcere, iar
   * omul rămânea blocat până expira browserul.
   *
   * Răgazul de după oprire există fiindcă `onend` vine cu textul deja în câmp: fără el,
   * butonul ar fugi de sub deget în aceeași frământare în care tocmai ai apăsat pe el.
   */
  private micHeldVisible(): boolean {
    return this.dictation?.listening === true || this.micSettleTimer !== undefined;
  }

  /** Ține microfonul descoperit încă `MIC_SETTLE_MS` după ce dictarea s-a oprit. */
  private holdMicVisible(): void {
    clearTimeout(this.micSettleTimer);
    this.micSettleTimer = setTimeout(() => {
      this.micSettleTimer = undefined;
      this.sync();
    }, MIC_SETTLE_MS);
  }

  private toggleDictation(): void {
    if (this.dictation?.listening) {
      this.dictation.stop();
      return;
    }
    const idle = this.opts.labels.placeholder;
    const listening = this.opts.labels.micListening ?? "Listening…";
    // instanță nouă per sesiune de dictare: unele browsere nu suportă restart
    this.dictation = createDictation(navigator.language, {
      onText: (text) => {
        this.setValue(this.dictationBase ? `${this.dictationBase} ${text}` : text);
        this.input.scrollTop = this.input.scrollHeight;
      },
      onHearing: () => {
        this.input.placeholder = `✓ ${listening}`;
      },
      onEnd: () => {
        this.micBtn?.classList.remove("aigc-mic--live");
        if (this.micBtn && this.opts.labels.mic) this.micBtn.title = this.opts.labels.mic;
        this.input.placeholder = idle;
        this.holdMicVisible();
        this.input.focus();
      },
      onError: (error) => {
        this.micBtn?.classList.remove("aigc-mic--live");
        this.input.placeholder = idle;
        // și pe eroare: `onend` nu e garantat după orice eșec, iar microfonul trebuie
        // să rămână apăsabil ca omul să poată reîncerca
        this.holdMicVisible();
        if (error === "not-allowed" || error === "service-not-allowed") this.opts.onMicError?.("denied");
        else if (error === "no-speech") this.opts.onMicError?.("no-speech");
        else if (error === "network") this.opts.onMicError?.("network");
        else if (error !== "aborted") this.opts.onMicError?.("failed");
      },
    });
    if (!this.dictation) return;
    this.dictationBase = this.input.value.trim();
    this.micBtn?.classList.add("aigc-mic--live");
    if (this.micBtn) this.micBtn.title = listening;
    this.input.placeholder = listening;
    this.dictation.start();
    // descoperă microfonul imediat, nu abia la primul cuvânt transcris
    this.sync();
  }
}
